import { useCallback, useRef, useState, useMemo, useId } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { FakeLink } from '@hivebrite/design-system/Link';
import { Loader } from '@hivebrite/design-system/Loader';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';
import { useIntersectionObserver, useToggle } from '@hivebrite/react-hooks';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { truncateHtmlAt } from 'hb-react/shared/utils/truncateHtmlAt';

import { useLanguageDetection } from './hooks/useLanguageDetection';
import { useTranslation } from './hooks/useTranslation';

const Content = ({
  type,
  id,
  attribute,
  lang,
  text,
  renderer,
  enabled = true,
  screenReadersTruncate: {
    htmlId: srHtmlId,
    limit: truncateAt,
    isTruncated,
  } = {},
}) => {
  const {
    text: translation,
    isLoading,
    isError,
  } = useTranslation(
    {
      type,
      id,
      text,
      attribute,
      lang,
    },
    {
      enabled,
    },
  );

  const truncatedTranslation = useMemo(() => {
    if (isLoading) {
      return;
    }

    if (translation && isTruncated && truncateAt) {
      return truncateHtmlAt(translation, truncateAt);
    }

    return translation;
  }, [isLoading, isTruncated, translation, truncateAt]);

  if (isLoading || isError) {
    return <Loader size={16} />;
  }

  if (typeof renderer === 'function') {
    return renderer({ translation });
  }

  return (
    <>
      {truncateAt && isTruncated && (
        <ScreenReadersContent id={srHtmlId}>
          <div
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: truncatedTranslation,
            }}
          />
        </ScreenReadersContent>
      )}
      <Wrapper
        aria-hidden={!!truncateAt}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: translation,
        }}
      />
    </>
  );
};

const Button = ({ a11yId, isOpen, onClick }) => (
  <div>
    <StyledButton
      aria-controls={a11yId}
      aria-expanded={isOpen}
      onClick={onClick}
    >
      {isOpen
        ? s__('Translations|See original')
        : s__('Translations|See translation')}
    </StyledButton>
  </div>
);

const TranslateContent = ({
  model: { id, type } = {},
  children,
  text,
  attribute,
  lang,
  renderer,
  screenReadersTruncate,
}) => {
  const a11yId = useId();
  const { isOpen, toggle } = useToggle();

  if (typeof renderer === 'function') {
    return (
      <Content
        type={type}
        id={id}
        attribute={attribute}
        text={text}
        lang={lang}
        renderer={({ translation }) =>
          renderer({ children, translation, isOpen, toggle })
        }
        enabled={isOpen}
        screenReadersTruncate={screenReadersTruncate}
      />
    );
  }

  return (
    <span>
      <span id={a11yId} style={{ display: 'inline-block', width: '100%' }}>
        {!isOpen ? (
          children
        ) : (
          <Content
            type={type}
            id={id}
            attribute={attribute}
            lang={lang}
            text={text}
            screenReadersTruncate={screenReadersTruncate}
          />
        )}
      </span>
      <Button a11yId={a11yId} isOpen={isOpen} onClick={toggle} />
    </span>
  );
};

const TranslateContentWithLanguageDetection = ({
  model,
  children,
  attribute,
  text,
  lang,
  renderer,
  screenReadersTruncate,
}) => {
  const ref = useRef();
  const [isVisible, setVisible] = useState(false);
  const { currentUser } = useDefaultAppContexts();

  const locale = currentUser?.locale || window.__HB_LOCALE__;

  useIntersectionObserver(
    ref,
    ([entry]) => entry.isIntersecting && !isVisible && setVisible(true),
  );

  const { lang: detectedLang = [], isLoading } = useLanguageDetection(
    {
      ...model,
      attribute,
      text,
      lang,
    },
    { enabled: isVisible },
  );

  const detectedLanguage = detectedLang.find(
    ({ confidence }) => confidence > 0.5,
  );

  if (isLoading || !detectedLanguage || detectedLanguage?.language === locale) {
    return <span ref={ref}>{children}</span>;
  }

  return (
    <TranslateContent
      model={model}
      attribute={attribute}
      text={text}
      lang={lang}
      renderer={renderer}
      children={children}
      screenReadersTruncate={screenReadersTruncate}
    />
  );
};

const guardProps = ({ id, user, type }) => !!(id && user && type);

const ContentWrapper = ({ children, text, model }) => {
  if (!text) {
    return children;
  }

  return (
    <TranslateContentWithLanguageDetection
      text={text}
      model={model}
      children={children}
    />
  );
};

const HtmlTranslation = ({ model, children }) => {
  const [text, setText] = useState(null);

  const setHtml = useCallback((el) => {
    if (el?.innerHTML) {
      setText(el.innerHTML);
    }
  }, []);

  return (
    <span ref={setHtml}>
      <ContentWrapper text={text} model={model}>
        {children}
      </ContentWrapper>
    </span>
  );
};

export const Translation = ({
  model = {},
  children,
  attribute,
  lang,
  renderer,
  htmlContent = false,
  detectLanguage = true,
  screenReadersTruncate,
}) => {
  const { currentUser, currentNetwork } = useDefaultAppContexts();

  const featureEnabled =
    currentNetwork?.network_configuration?.google_cloud_translate?.enabled;
  const isOwnPost = currentUser?.id === model?.user?.id;

  if (!featureEnabled || !guardProps(model) || isOwnPost) {
    return children;
  }

  if (htmlContent) {
    return <HtmlTranslation model={model}>{children}</HtmlTranslation>;
  }

  if (detectLanguage) {
    return (
      <TranslateContentWithLanguageDetection
        model={model}
        attribute={attribute}
        lang={lang}
        renderer={renderer}
        children={children}
        screenReadersTruncate={screenReadersTruncate}
      />
    );
  }

  return (
    <TranslateContent
      model={model}
      attribute={attribute}
      lang={lang}
      renderer={renderer}
      children={children}
      screenReadersTruncate={screenReadersTruncate}
    />
  );
};

const Wrapper = styled(Box)`
  all: inherit;
`;

const StyledButton = styled(FakeLink).attrs({
  font: 'metadata-regular-asphalt',
  role: 'button',
})`
  user-select: none;
  display: inline;
`;
